import { useState, useEffect, useCallback } from "react";
import {
  getAtRiskUsers,
  getDailySnapshots,
  getCohortData,
  getEngagementMetrics,
  getRetentionSuggestions,
  getRetentionMetrics,
  calculateAllRetentionData,
} from "services/retention-service";

export const useRetentionData = (filter = "monthly") => {
  const [data, setData] = useState({
    metrics: null,
    dailySnapshots: [],
    cohortData: [],
    engagementMetrics: null,
    suggestions: [],
    atRiskUsers: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Individual loading states for each data source
  const [loadingStates, setLoadingStates] = useState({
    metrics: false,
    snapshots: false,
    cohorts: false,
    engagement: false,
  });

  const fetchRetentionData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Now fetch all data in parallel
      let cohortCount, daysToFetch;

      switch (filter) {
        case "1year":
          cohortCount = 52;
          daysToFetch = 365;
          break;
        case "6months":
          cohortCount = 26;
          daysToFetch = 180;
          break;
        case "3months":
          cohortCount = 13;
          daysToFetch = 90;
          break;
        case "1month":
          cohortCount = 4;
          daysToFetch = 30;
          break;
        case "2weeks":
          cohortCount = 2;
          daysToFetch = 14;
          break;
        case "1week":
          cohortCount = 1;
          daysToFetch = 7;
          break;
        case "alltime":
          cohortCount = 52;
          daysToFetch = 1825; // 5 years - a reasonable "all time" limit
          break;
        default:
          cohortCount = 26;
          daysToFetch = 180;
      }

      // Initialize empty data and loading states
      const initialData = {
        metrics: null,
        dailySnapshots: [],
        cohortData: [],
        engagementMetrics: null,
        suggestions: [],
        atRiskUsers: [],
      };
      setData(initialData);
      setLoadingStates({
        metrics: true,
        snapshots: true,
        cohorts: true,
        engagement: true,
      });

      let errors = [];
      let fetchedMetrics = null;

      // Fetch metrics (usually fast)
      try {
        fetchedMetrics = await getRetentionMetrics(filter);
        setData((prev) => ({
          ...prev,
          metrics: fetchedMetrics,
          suggestions: getRetentionSuggestions(fetchedMetrics),
        }));
      } catch (err) {
        console.error("Failed to fetch retention metrics:", err);
        errors.push("Retention metrics");
      } finally {
        setLoadingStates((prev) => ({ ...prev, metrics: false }));
      }

      // Fetch snapshots (usually fast)
      try {
        const snapshots = await getDailySnapshots(daysToFetch);
        setData((prev) => ({ ...prev, dailySnapshots: snapshots }));
      } catch (err) {
        console.error("Failed to fetch daily snapshots:", err);
        errors.push("Daily snapshots");
      } finally {
        setLoadingStates((prev) => ({ ...prev, snapshots: false }));
      }

      // Fetch cohorts (usually fast)
      try {
        const cohorts = await getCohortData(cohortCount, filter);
        setData((prev) => ({ ...prev, cohortData: cohorts }));
      } catch (err) {
        console.error("Failed to fetch cohort data:", err);
        errors.push("Cohort data");
      } finally {
        setLoadingStates((prev) => ({ ...prev, cohorts: false }));
      }

      // Fetch engagement (can be slow for large date ranges)
      try {
        const engagement = await getEngagementMetrics(filter);
        setData((prev) => ({ ...prev, engagementMetrics: engagement }));
      } catch (err) {
        console.error("Failed to fetch engagement metrics:", err);
        errors.push("Engagement metrics");
      } finally {
        setLoadingStates((prev) => ({ ...prev, engagement: false }));
      }

      // Check if we need to calculate historical data
      if (!fetchedMetrics || !fetchedMetrics.dailyMetrics) {
        console.log("No retention data found, calculating...");
        try {
          await calculateAllRetentionData();
          // Wait a moment for data to be written
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Refetch after calculation
          const newMetrics = await getRetentionMetrics(filter);
          setData((prev) => ({
            ...prev,
            metrics: newMetrics,
            suggestions: getRetentionSuggestions(newMetrics),
          }));
        } catch (calcErr) {
          console.error("Failed to calculate retention data:", calcErr);
          errors.push("Data calculation");
        }
      }

      setLastUpdated(new Date());

      // Set error message only if everything failed
      if (errors.length > 0) {
        setError(
          errors.length === 4
            ? "Failed to load all dashboard data"
            : `Partial data load failure: ${errors.join(", ")} unavailable`
        );
      }
    } catch (err) {
      console.error("Error fetching retention data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchAtRiskUsers = useCallback(
    async (riskType = null, limit = 100) => {
      try {
        const users = await getAtRiskUsers(riskType, limit, filter);
        setData((prev) => ({ ...prev, atRiskUsers: users.users || users }));
        return users.users || users;
      } catch (err) {
        console.error("Error fetching at-risk users:", err);
        throw err;
      }
    },
    [filter]
  );

  const refreshData = useCallback(() => {
    fetchRetentionData();
  }, [fetchRetentionData]);

  useEffect(() => {
    fetchRetentionData();
  }, [fetchRetentionData]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    loadingStates, // Individual loading states
    fetchRetentionData,
    fetchAtRiskUsers,
  };
};

export const useRetentionMetrics = (filter = "monthly") => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const data = await getRetentionMetrics(); // Backend will calculate if needed
        setMetrics(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [filter]);

  return { metrics, loading, error };
};

export const useCohortData = (count = 12, filter = "monthly") => {
  const [cohortData, setCohortData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCohortData = async () => {
      try {
        setLoading(true);
        const data = await getCohortData(count, filter);
        setCohortData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCohortData();
  }, [count, filter]);

  return { cohortData, loading, error };
};

export const useAtRiskUsers = (
  riskType = null,
  limit = 100,
  filter = "6months"
) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAtRiskUsers(riskType, limit, filter);
      setUsers(data.users || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [riskType, limit, filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, error, refetch: fetchUsers };
};
